import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../services/authService";
import "../styles/ReturnRequest.css";

export default function ReturnRequest() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [order, setOrder] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/orders/${orderId}`, {
        credentials: "include",
      });

      if (!response.ok) {
        navigate("/orders");
        return;
      }

      const data = await response.json();

      if (!canRequestReturn(data)) {
        navigate(`/orders/${orderId}`);
        return;
      }

      setOrder(data);

      const initialQuantities = {};

      data.items.forEach((item) => {
        initialQuantities[item.id] = 0;
      });

      setQuantities(initialQuantities);
    } catch {
      setError(t("returns.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const canRequestReturn = (orderData) => {
    if (
      !orderData ||
      orderData.status !== "DELIVERED" ||
      !orderData.deliveredAt
    ) {
      return false;
    }

    const deliveredAt = new Date(orderData.deliveredAt);
    const deadline = new Date(deliveredAt);

    deadline.setDate(deadline.getDate() + 14);

    return new Date() <= deadline;
  };

  const selectedItems = useMemo(() => {
    if (!order) {
      return [];
    }

    return order.items
      .filter((item) => Number(quantities[item.id] || 0) > 0)
      .map((item) => ({
        ...item,
        returnQuantity: Number(quantities[item.id]),
      }));
  }, [order, quantities]);

  const estimatedRefund = useMemo(() => {
    return selectedItems.reduce(
      (total, item) =>
        total + Number(item.unitPrice) * item.returnQuantity,
      0
    );
  }, [selectedItems]);

  const updateQuantity = (item, value) => {
    const parsedValue = Number(value);

    const validQuantity = Math.max(
      0,
      Math.min(
        Number.isFinite(parsedValue) ? parsedValue : 0,
        item.quantity
      )
    );

    setQuantities((current) => ({
      ...current,
      [item.id]: validQuantity,
    }));
  };

  const submitReturn = async (event) => {
    event.preventDefault();

    if (selectedItems.length === 0) {
      setError(t("returns.selectProductError"));
      return;
    }

    if (!reason) {
      setError(t("returns.reasonRequired"));
      return;
    }

    setSending(true);
    setError("");

    try {
      const csrf = await getCsrfToken();

      const response = await fetch("/api/returns", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify({
          orderId: order.id,
          reason,
          customerComments: comments,
          items: selectedItems.map((item) => ({
            orderItemId: item.id,
            quantity: item.returnQuantity,
          })),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);

        setError(
          body?.message ||
            body?.detail ||
            t("returns.createError")
        );

        return;
      }

      const createdReturn = await response.json();

      navigate(`/returns/${createdReturn.id}`);
    } catch {
      setError(t("returns.createError"));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <main className="return-request-page">
        <div className="return-request-loading">
          {t("returns.loading")}
        </div>
      </main>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <main className="return-request-page">
      <section className="return-request-container">
        <button
          type="button"
          className="profile-back-btn"
          onClick={() => navigate(`/orders/${order.id}`)}
        >
          ← {t("returns.backToOrder")}
        </button>

        <div className="return-request-heading">
          <div>
            <h1>{t("returns.requestTitle")}</h1>
            <p>
              {t("returns.orderLabel")}:{" "}
              <strong>{order.orderNumber}</strong>
            </p>
          </div>

          <span className="return-request-deadline">
            {t("returns.deadline", {
              date: new Date(
                new Date(order.deliveredAt).getTime() +
                  14 * 24 * 60 * 60 * 1000
              ).toLocaleDateString(),
            })}
          </span>
        </div>

        <form onSubmit={submitReturn}>
          <section className="return-request-block">
            <h2>{t("returns.selectProducts")}</h2>
            <p className="return-request-help">
              {t("returns.selectProductsHelp")}
            </p>

            <div className="return-request-items">
              {order.items.map((item) => {
                const selectedQuantity =
                  Number(quantities[item.id] || 0);

                return (
                  <article
                    className={`return-request-item ${
                      selectedQuantity > 0 ? "selected" : ""
                    }`}
                    key={item.id}
                  >
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.productName}
                      />
                    )}

                    <div className="return-request-item-info">
                      <h3>{item.productName}</h3>

                      <p>
                        {item.quantity} x{" "}
                        {Number(item.unitPrice).toFixed(2)} €
                      </p>

                      <strong>
                        {Number(item.subtotal).toFixed(2)} €
                      </strong>
                    </div>

                    <label>
                      {t("returns.quantity")}

                      <select
                        value={selectedQuantity}
                        onChange={(event) =>
                          updateQuantity(item, event.target.value)
                        }
                      >
                        {Array.from(
                          { length: item.quantity + 1 },
                          (_, index) => (
                            <option key={index} value={index}>
                              {index}
                            </option>
                          )
                        )}
                      </select>
                    </label>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="return-request-block">
            <h2>{t("returns.returnDetails")}</h2>

            <label className="return-request-field">
              {t("returns.reason")}

              <select
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                required
              >
                <option value="">
                  {t("returns.selectReason")}
                </option>

                <option value="WRONG_PRODUCT">
                  {t("returns.reasons.WRONG_PRODUCT")}
                </option>

                <option value="NO_LONGER_NEEDED">
                  {t("returns.reasons.NO_LONGER_NEEDED")}
                </option>

                <option value="INCOMPATIBLE">
                  {t("returns.reasons.INCOMPATIBLE")}
                </option>

                <option value="DAMAGED">
                  {t("returns.reasons.DAMAGED")}
                </option>

                <option value="DEFECTIVE">
                  {t("returns.reasons.DEFECTIVE")}
                </option>

                <option value="OTHER">
                  {t("returns.reasons.OTHER")}
                </option>
              </select>
            </label>

            <label className="return-request-field">
              {t("returns.comments")}

              <textarea
                value={comments}
                maxLength={1000}
                rows={5}
                placeholder={t("returns.commentsPlaceholder")}
                onChange={(event) =>
                  setComments(event.target.value)
                }
              />

              <small>{comments.length}/1000</small>
            </label>
          </section>

          <section className="return-request-summary">
            <div>
              <span>{t("returns.selectedUnits")}</span>
              <strong>
                {selectedItems.reduce(
                  (total, item) =>
                    total + item.returnQuantity,
                  0
                )}
              </strong>
            </div>

            <div>
              <span>{t("returns.estimatedRefund")}</span>
              <strong>{estimatedRefund.toFixed(2)} €</strong>
            </div>

            <p>{t("returns.refundNotice")}</p>
          </section>

          {error && (
            <p className="return-request-error">{error}</p>
          )}

          <button
            type="submit"
            className="return-request-submit"
            disabled={sending || selectedItems.length === 0}
          >
            {sending
              ? t("returns.sending")
              : t("returns.submit")}
          </button>
        </form>
      </section>
    </main>
  );
}